import { isWechatMiniProgramWebView } from '@/libs/functions/ua'

const SHARE_MESSAGE_DELAY = 1500

const DIRECT_SHARE_GOODS_TEMPLATE_ROUTE_PATTERN =
  /^\/goods\/template\/(?:FT\/product|QR)$/

const SHARE_ROUTE_PATTERNS = [
  /^\/promo\/template\/(?:pointsPlate|equityPlate|lottery(?:\/mysteryBox)?|nselectXPlate(?:\/(?:detail|record|overdue))?)$/,
  /^\/equity\/mixp\/(?:index|list)$/,
  DIRECT_SHARE_GOODS_TEMPLATE_ROUTE_PATTERN,
  /^\/goods\/equity\/detail$/,
  /^\/verification\/station$/,
  /^\/verification\/network(?:\/(?:cip|template\/(?:DG|goodCard)))?$/,
  /^\/goods\/hotel\/network$/,
  /^\/goods\/template\/JD\/base\/network$/,
  /^\/goods\/template\/TPS\/base\/network$/,
  /^\/goods\/template\/SC\/specs$/,
  /^\/goods\/template\/(?:GQR\/(?:networkList|station)|SC\/network|SCG\/network|TPS\/network|common\/(?:base|lcg)\/networkList)$/,
  /^\/limousine\/template\/LS\/network\/(?:list|station|view)$/,
  /^\/limousine\/template\/(?:SN|CC)\/address\/network$/,
  /^\/vvip\/network\/list$/,
  /^\/promo\/template\/activity\/section$/,
  /^\/promo\/template\/activity\/static$/,
]

const SHARE_PRODUCT_ROUTE_PATTERNS = [
  /^\/vvip(?:\/template\/(?:base|HSG))?$/,
  /^\/limousine(?:\/template\/(?:LS|SN|CC|DJ))?$/,
]

const SHARE_LOADING_CATEGORY_TYPES = [
  'GC',
  'GV',
  'JD',
  'SC',
  'SW',
  'CP',
  'TPS',
  'LCG',
  'OCG',
  'SCG',
  'SSC',
  'GQR',
]

const GOODS_TEMPLATE_ROUTE_PATTERN = /^\/goods\/template\/.+$/

type RouteLocation = Pick<Location, 'pathname' | 'search'>

let isWaitingForGoodsLoadingLanding = false
let pendingBareProductLandingPaths: string[] = []

function isWhitelistedLoadingRoute(
  pathname: string,
  searchParams: URLSearchParams,
) {
  return (
    pathname === '/goods/loading' &&
    SHARE_LOADING_CATEGORY_TYPES.includes(
      searchParams.get('categoryType') || '',
    )
  )
}

function shouldSkipGoodsLoadingLandingReport({
  pathname,
  search,
}: RouteLocation) {
  const isGoodsLoadingRoute = isWhitelistedLoadingRoute(
    pathname,
    new URLSearchParams(search),
  )
  const shouldSkip =
    isWaitingForGoodsLoadingLanding &&
    GOODS_TEMPLATE_ROUTE_PATTERN.test(pathname) &&
    !DIRECT_SHARE_GOODS_TEMPLATE_ROUTE_PATTERN.test(pathname)

  isWaitingForGoodsLoadingLanding = isGoodsLoadingRoute
  return shouldSkip
}

function getBareProductLandingPaths(pathname: string) {
  if (pathname === '/vvip') {
    return ['/vvip', '/vvip/template/base', '/vvip/template/HSG']
  }
  if (pathname === '/limousine') {
    return ['/limousine', '/limousine/template/LS']
  }
  return [pathname]
}

function shouldSkipBareProductLandingReport({
  pathname,
  search,
}: RouteLocation) {
  const hasSearchParams = Boolean(new URLSearchParams(search).toString())
  const isProductRoute = SHARE_PRODUCT_ROUTE_PATTERNS.some((pattern) =>
    pattern.test(pathname),
  )
  const shouldSkip =
    !hasSearchParams && pendingBareProductLandingPaths.includes(pathname)

  pendingBareProductLandingPaths =
    hasSearchParams && isProductRoute
      ? getBareProductLandingPaths(pathname)
      : []
  return shouldSkip
}

function getShareH5Url({ pathname, search }: RouteLocation, href: string) {
  const searchParams = new URLSearchParams(search)
  const isWhitelistedRoute = SHARE_ROUTE_PATTERNS.some((pattern) =>
    pattern.test(pathname),
  )
  const isWhitelistedProductRoute =
    Boolean(searchParams.toString()) &&
    SHARE_PRODUCT_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
  const isWhitelistedLoading = isWhitelistedLoadingRoute(pathname, searchParams)
  const h5Url =
    isWhitelistedRoute || isWhitelistedProductRoute || isWhitelistedLoading
      ? encodeURIComponent(href)
      : ''

  console.log('[miniProgram] share route', { pathname, search, h5Url })
  return h5Url
}

export function reportShareToMiniProgram(
  wxSdkReady: Promise<unknown>,
  location: RouteLocation,
) {
  if (!isWechatMiniProgramWebView()) {
    return
  }

  const shouldSkipGoodsLanding = shouldSkipGoodsLoadingLandingReport(location)
  const shouldSkipBareProductLanding =
    shouldSkipBareProductLandingReport(location)

  if (shouldSkipGoodsLanding || shouldSkipBareProductLanding) {
    console.log('[miniProgram] skip landing postMessage', {
      location,
      shouldSkipGoodsLanding,
      shouldSkipBareProductLanding,
    })
    return
  }

  const h5Url = getShareH5Url(location, window.location.href)
  if (!h5Url) {
    console.log('[miniProgram] skip non-whitelisted postMessage', location)
    return
  }

  window.setTimeout(async () => {
    try {
      await wxSdkReady
    } catch {
      return
    }

    if (!window.wx?.miniProgram?.postMessage) {
      return
    }
    const data = {
      type: 'share',
      h5Url,
      saasAuth: '1',
      title: document.title,
      imageUrl: '0',
    }

    console.log('[miniProgram] postMessage', data)
    window.wx.miniProgram.postMessage({ data })
  }, SHARE_MESSAGE_DELAY)
}
